import { type ReactElement, useEffect, useMemo, useState } from 'react';
import api from '@/api';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
} from '~/hooks/useAsyncPeoplePicker';
import { type Option, SearchSelect } from '~/styles/forms/SearchSelect';

export interface GroupStudentSearchFilterProps {
  groupId: number;
  /** The labels the filters endpoint resolved for the remembered selection. */
  selectedOptions: readonly Option[];
  value: string[];
  onChange: (value: string[]) => void;
}

/**
 * A filter panel's Student Name control for a group table.
 *
 * A group can hold tens of thousands of students, so the control searches the
 * roster as the people pickers do rather than listing it. The value stays with
 * the panel; this only has to know a label for each id in it.
 */
export function GroupStudentSearchFilter({
  groupId,
  selectedOptions,
  value,
  onChange,
}: GroupStudentSearchFilterProps): ReactElement {
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  // Labels of the options picked here. The endpoint only names the selection
  // the table was opened with.
  const [pickedLabels, setPickedLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    const timer = setTimeout(() => setSearch(term), PEOPLE_PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const { data: matches, isFetching } = api.getGroupStudents.useQuery(
    {
      path: { group_id: groupId },
      query: { search, limit: PEOPLE_PICKER_PAGE_SIZE },
    },
    { enabled: groupId > 0 && search.length >= PEOPLE_PICKER_MIN_QUERY },
  );

  const matchOptions = useMemo<Option[]>(
    () =>
      (matches?.data ?? []).map((student) => ({
        value: String(student.id),
        label: student.display_name,
      })),
    [matches],
  );

  const options = useMemo<Option[]>(() => {
    const resolved = new Map(selectedOptions.map((o) => [o.value, o.label]));
    const selected = value.map((id) => ({
      value: id,
      // A student who has since left the group has no label to resolve, and a
      // chip with no option can't be seen or removed.
      label: resolved.get(id) ?? pickedLabels[id] ?? `#${id}`,
    }));
    return [
      ...selected,
      ...matchOptions.filter((option) => !value.includes(option.value)),
    ];
  }, [value, selectedOptions, pickedLabels, matchOptions]);

  const handleChange = (next: string[]): void => {
    const picked = matchOptions.filter((option) => next.includes(option.value));
    if (picked.length > 0) {
      setPickedLabels((prev) => ({
        ...prev,
        ...Object.fromEntries(picked.map((o) => [o.value, o.label])),
      }));
    }
    onChange(next);
  };

  return (
    <SearchSelect
      options={options}
      value={value}
      onChange={handleChange}
      onSearch={setTerm}
      minQueryLength={PEOPLE_PICKER_MIN_QUERY}
      // Through the debounce window too, so the control doesn't flash "no
      // results" between a keystroke and the request it caused.
      loading={
        term.length >= PEOPLE_PICKER_MIN_QUERY &&
        (term !== search || isFetching)
      }
      variant="filter"
    />
  );
}
